import { useEffect, useRef, useState } from 'react'
import { useNodeLogs, usePersistedNodeLogs } from '@/hooks'
import { useParams } from 'react-router-dom'
import type { WorkflowRunNode } from '@/types'
import { Loader2, FileText } from 'lucide-react'
import { Button } from '@/components/ui'
import { CopyButton } from '@/components/ui/copy-button'
import { cn } from '@/lib/utils'

type LogFilter = 'all' | 'system' | 'errors'

function filterLogs(raw: string, filter: LogFilter): string {
  if (filter === 'all') return raw
  const prefix = filter === 'system' ? '[system]' : '[error]'
  return raw
    .split('\n')
    .filter(line => line.startsWith(prefix))
    .join('\n')
}

interface Props {
  node: WorkflowRunNode
}

const CONTAINER_OUTPUT_HEADER = '[container] --- Container Output ---'

function resolveDisplayedLogs(baseLogs: string, fullLogs: string | null): string | null {
  if (fullLogs === null) {
    return baseLogs || null
  }
  if (!baseLogs) {
    return fullLogs
  }
  const separator = baseLogs.includes(CONTAINER_OUTPUT_HEADER) ? '\n' : `\n\n${CONTAINER_OUTPUT_HEADER}\n`
  return `${baseLogs}${separator}${fullLogs}`
}

export function LogViewer({ node }: Props) {
  const { projectId, runId } = useParams()
  const isRunning = node.status === 'Running' || node.status === 'RetryPending'
  const containerRef = useRef<HTMLDivElement>(null)
  const shouldAutoScroll = useRef(true)
  const [loadPersisted, setLoadPersisted] = useState(false)
  const [logFilter, setLogFilter] = useState<LogFilter>('all')

  const { data, isLoading } = useNodeLogs(
    projectId!,
    runId!,
    node.id,
    node.status,
  )

  const persisted = usePersistedNodeLogs(projectId!, runId!, node.id, loadPersisted)

  const persistedLogs = persisted.data?.logs ?? null
  const liveContainerLogsAvailable = data?.liveContainerLogsAvailable ?? false
  const noPersistedLogs = loadPersisted && !persisted.isFetching && !persisted.isError && persistedLogs === null

  const canLoadFullLogs =
    !isRunning && node.containerStrategy !== 'None' && !liveContainerLogsAvailable && !persistedLogs && !noPersistedLogs

  const baseLogs = data?.hasLogs ? data.logs ?? '' : ''
  const displayedLogs = resolveDisplayedLogs(baseLogs, persistedLogs)

  useEffect(() => {
    if (shouldAutoScroll.current && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight
    }
  }, [data?.logs, persistedLogs])

  const handleScroll = () => {
    if (!containerRef.current) return
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current
    shouldAutoScroll.current = scrollHeight - scrollTop - clientHeight < 100
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full bg-neutral" role="status" aria-live="polite">
        <Loader2 className="h-5 w-5 animate-spin text-neutral-foreground" />
      </div>
    )
  }

  if (!displayedLogs) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-neutral text-neutral-foreground text-sm p-6">
        {isRunning ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin mb-2 text-info-foreground" />
            <p className="text-info-foreground">Waiting for logs...</p>
          </>
        ) : (
          <>
            <p>No logs available.</p>
            {node.errorMessage && (
              <p className="text-xs mt-3 text-danger-foreground max-w-md text-center">{node.errorMessage}</p>
            )}
          </>
        )}
      </div>
    )
  }

  const filteredLogs = filterLogs(displayedLogs, logFilter)

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center gap-1 px-3 py-1.5 bg-neutral border-b border-neutral-foreground shrink-0">
        {(['all', 'system', 'errors'] as LogFilter[]).map(f => (
          <button
            key={f}
            onClick={() => setLogFilter(f)}
            className={cn(
              'px-2.5 py-0.5 rounded text-xs font-medium transition-colors',
              logFilter === f
                ? 'bg-neutral text-white'
                : 'text-neutral-foreground hover:text-neutral-foreground hover:bg-neutral'
            )}
          >
            {f === 'all' ? 'All' : f === 'system' ? 'System' : 'Errors'}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          {canLoadFullLogs && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 border-gray-700 bg-neutral text-neutral-foreground hover:bg-neutral hover:text-white"
              disabled={persisted.isFetching}
              onClick={() => (loadPersisted ? persisted.refetch() : setLoadPersisted(true))}
              aria-label="Load full logs"
              data-testid="load-full-logs-button"
            >
              {persisted.isFetching ? (
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              ) : (
                <FileText className="h-3 w-3 mr-1" />
              )}
              Load full logs
            </Button>
          )}
          <CopyButton
            text={displayedLogs}
            tooltipLabel="Copy logs"
            className="bg-gray-800/80 hover:bg-neutral [&_svg]:text-gray-300 hover:[&_svg]:text-white"
          />
        </div>
      </div>
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden bg-neutral p-4 font-mono text-xs leading-relaxed text-neutral-foreground whitespace-pre-wrap break-words"
      >
        {filteredLogs || (logFilter !== 'all' && (
          <p className="text-gray-500">No {logFilter} log lines found.</p>
        ))}
        {isRunning && logFilter === 'all' && (
          <span className="inline-block w-2 h-4 bg-info animate-pulse ml-0.5" />
        )}
        {persisted.isError && (
          <p role="alert" className="mt-3 text-danger-foreground">Failed to load full logs.</p>
        )}
        {noPersistedLogs && (
          <p className="mt-3 text-gray-500">No persisted logs were stored for this node.</p>
        )}
      </div>
    </div>
  )
}
